/* LUSEDAGBOK — anleggets egen trykte lusebok, laget og bestilt herfra.
 *
 * Én ting per skjerm og én knapp som tar deg videre, fordi dette gjøres
 * sjelden, ofte på telefonen, og av noen som vil ha bøker og ikke et skjema:
 *
 *   start → anlegget → merdene → slik blir boken → bestill → takk
 *
 * og for den som har bestilt før: start → bestill, med «Bestill igjen».
 *
 * MERDENE ER DE EKTE. Numrene i boka er numrene tellingene føres mot, så
 * «nummerer på nytt» lagrer anleggets oppsett (PUT /layout) og ikke en kopi
 * for boka. Har anlegget ikke noe oppsett, velges et ferdig ett; ligger det
 * annerledes, finnes karteditoren.
 *
 * FORHÅNDSVISNINGEN ER TRYKKFILEN. Sidene som vises er bildene skriveren lagde
 * av den samme PDF-en som sendes til trykk, så «Ser bra ut» sies om det som
 * faktisk blir trykt.
 *
 * Designet er godkjent i Claude Design («Lusedagbok – bestilling»).
 */
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react'
import {
  ApiError, liceBookPath, liceBookPdf, liceBookPreview, makeLiceBook,
  orderLiceBook, saveSiteLayout,
  type LiceBook, type LiceBookDelivery, type LiceBookDraft, type Me,
  type SiteLayout,
} from '../api'
import { invalidate, useApi } from '../useApi'
import { ErrorBox, Rows, placeName } from '../components/bits'
import { Link } from '../router'
import { centroid, toLocal, DEFAULT_CIRCUMFERENCE_M } from '../merdgeo'
import { OPPSETT, enheter, plasser, type Oppsett } from '../merdoppsett'
import { MerdKartNummer, kanViseKart } from '../components/MerdKartNummer'

type Steg = 'start' | 'anlegget' | 'merdene' | 'boken' | 'bestill' | 'takk'
// Bøkene selges i pakker på fire, som på serveren.
const PAKKE = 4

const FREMDRIFT: Steg[] = ['anlegget', 'merdene', 'boken', 'bestill']

const SIDENAVN = ['Forsiden', 'Side 1 · om boken og QR-koden',
                  'Slik fyller du ut', 'Et telleark', 'Baksiden']

function feil(e: unknown): string {
  if (e instanceof ApiError) {
    return typeof e.detail === 'string' ? e.detail : e.code
  }
  return 'Noe gikk galt. Prøv igjen.'
}

export function Lusedagbok({ id }: { id: string; me: Me }) {
  const draftQ = useApi<LiceBookDraft>(liceBookPath(id))
  const layoutQ = useApi<SiteLayout>(`/v1/sites/${encodeURIComponent(id)}/layout`)

  const [steg, setSteg] = useState<Steg>(() =>
    new URLSearchParams(window.location.search).get('steg') === 'merdene'
      ? 'merdene' : 'start')
  const [igjen, setIgjen] = useState(false)          // kom via «Bestill igjen»
  const [generasjon, setGenerasjon] = useState<string | null>(null)
  const [settefisk, setSettefisk] = useState<string | null>(null)
  const [bok, setBok] = useState<LiceBook | null>(null)
  const [bokFor, setBokFor] = useState('')           // hva boka ble laget av
  const [antall, setAntall] = useState(PAKKE)
  const [levering, setLevering] = useState<LiceBookDelivery | null>(null)
  const [opptatt, setOpptatt] = useState(false)
  const [melding, setMelding] = useState<string | null>(null)

  const draft = draftQ.data
  const layout = layoutQ.data

  // Forslagene fylles inn én gang, og blir stående om personen endrer dem.
  useEffect(() => {
    if (!draft) return
    setGenerasjon((g) => g ?? draft.generation?.name ?? '')
    setSettefisk((s) => s ?? draft.generation?.origin ?? '')
    setLevering((l) => l ?? draft.delivery ?? {
      recipient: '', address: '', postcode: '', city: '', phone: null })
  }, [draft])

  if (draftQ.error) return <ErrorBox error={draftQ.error} />
  if (draftQ.cold || layoutQ.cold || !draft || !layout) return <Rows n={4} />

  const navn = placeName(draft.site.name)
  const sist = draft.books?.find((b) => b.orders?.length) ?? draft.books?.[0] ?? null
  const gaa = (s: Steg) => { setMelding(null); setSteg(s) }

  /* Boka lages når forhåndsvisningen åpnes, og bare på nytt hvis noe som
     trykkes er endret siden sist. */
  const nokkel = JSON.stringify([generasjon, settefisk,
    layout.pens.map((p) => [p.pen_id, p.pen_no])])

  async function lagBok() {
    setOpptatt(true); setMelding(null)
    try {
      const r = await makeLiceBook(id, {
        generation: generasjon || null, hatchery: settefisk || null,
        sheets: draft!.sheets_default ?? 56,
      })
      setBok(r.book); setBokFor(nokkel)
      invalidate(liceBookPath(id))
    } catch (e) {
      setMelding(feil(e))
    } finally {
      setOpptatt(false)
    }
  }

  async function bestill() {
    if (!bok || !levering) return
    setOpptatt(true); setMelding(null)
    try {
      await orderLiceBook(id, bok.book_id, {
        ...levering, phone: levering.phone || null, qty: antall })
      invalidate(liceBookPath(id))
      gaa('takk')
    } catch (e) {
      setMelding(feil(e))
    } finally {
      setOpptatt(false)
    }
  }

  const tilbake = () => {
    if (steg === 'bestill' && igjen) return gaa('start')
    const i = FREMDRIFT.indexOf(steg)
    gaa(i > 0 ? FREMDRIFT[i - 1]! : 'start')
  }

  return (
    <div className="ldb">
      <div className="ldb-top">
        {steg !== 'start' && steg !== 'takk'
          ? <button className="btn ghost sm" onClick={tilbake}>‹ Tilbake</button>
          : <span />}
        {FREMDRIFT.includes(steg) && !igjen && (
          <ol className="steps" aria-label={`Steg ${FREMDRIFT.indexOf(steg) + 1} av 4`}>
            {FREMDRIFT.map((s, i) => (
              <li key={s} className={s === steg ? 'on'
                : i < FREMDRIFT.indexOf(steg) ? 'done' : undefined} />
            ))}
          </ol>
        )}
      </div>

      {steg === 'start' && (
        <Start draft={draft} navn={navn} sist={sist} siteId={id}
          onNy={() => { setIgjen(false); gaa('anlegget') }}
          onIgjen={() => {
            if (!sist) return
            setIgjen(true); setBok(sist); setBokFor(''); gaa('bestill')
          }} />
      )}

      {steg === 'anlegget' && (
        <section className="ldb-steg">
          <h1 className="display">Stemmer dette?</h1>
          <p className="lead">Dette trykkes på omslaget og på side 1.</p>
          <dl className="ldb-fakta">
            {draft.site.company && <><dt>Selskap</dt><dd>{draft.site.company}</dd></>}
            <dt>Lokalitet</dt><dd>{navn}</dd>
            <dt>Lokalitetsnummer</dt><dd className="num">{draft.site.locality_no ?? '–'}</dd>
          </dl>
          <label className="field">
            <span>Generasjon</span>
            <input type="text" value={generasjon ?? ''} maxLength={60}
              onChange={(e) => setGenerasjon(e.target.value)} />
          </label>
          <label className="field">
            <span>Settefiskanlegg</span>
            <input type="text" value={settefisk ?? ''} maxLength={120}
              onChange={(e) => setSettefisk(e.target.value)} />
          </label>
          {draft.generation?.source && (
            <p className="ldb-kilde">
              {draft.generation.source === 'farm'
                ? 'Fra fiskegruppen som står i sjøen nå. Du kan endre det.'
                : 'Hentet fra Phoniq IQ. Du kan endre det.'}
            </p>
          )}
          <p className="ldb-kilde">
            Feil navn eller nummer? Si fra til Phoniq, så retter vi det.
          </p>
          <button className="btn primary ldb-cta" onClick={() => gaa('merdene')}>
            Videre
          </button>
        </section>
      )}

      {steg === 'merdene' && (
        <Merdene id={id} draft={draft} layout={layout}
          onFerdig={async () => {
            await layoutQ.reloadAsync()
            gaa('boken')
          }} />
      )}

      {steg === 'boken' && (
        <Boken id={id} bok={bok} gammel={bokFor !== nokkel} opptatt={opptatt}
          melding={melding} tilgjengelig={draft.available !== false}
          onLag={() => void lagBok()} onVidere={() => gaa('bestill')} />
      )}

      {steg === 'bestill' && bok && levering && (
        <section className="ldb-steg">
          <h1 className="display">Bestill</h1>
          {igjen && (
            <p className="ldb-merke">
              Samme bok som sist · {bok.pens.length} merder · {bok.pages} sider
            </p>
          )}
          <div className="ldb-teller">
            <div>
              <strong>Antall bøker</strong>
              <span>Pakker à {PAKKE}, {bok.sheets} telleark i hver</span>
            </div>
            <div className="row" style={{ gap: 4 }}>
              <button className="btn sm" aria-label="Færre bøker"
                disabled={antall <= PAKKE} onClick={() => setAntall(antall - PAKKE)}>−</button>
              <span className="ldb-tall" aria-live="polite">{antall}</span>
              <button className="btn sm" aria-label="Flere bøker"
                disabled={antall >= 48} onClick={() => setAntall(antall + PAKKE)}>+</button>
            </div>
          </div>
          <fieldset className="ldb-adresse">
            <legend>Sendes til</legend>
            <label className="field"><span>Mottaker</span>
              <input type="text" value={levering.recipient} autoComplete="name" maxLength={120}
                onChange={(e) => setLevering({ ...levering, recipient: e.target.value })} />
            </label>
            <label className="field"><span>Adresse</span>
              <input type="text" value={levering.address} autoComplete="street-address" maxLength={200}
                onChange={(e) => setLevering({ ...levering, address: e.target.value })} />
            </label>
            <div className="ldb-post">
              <label className="field"><span>Postnr.</span>
                <input type="text" value={levering.postcode} inputMode="numeric" autoComplete="postal-code"
                  maxLength={4} pattern="[0-9]{4}"
                  onChange={(e) => setLevering({ ...levering,
                    postcode: e.target.value.replace(/\D/g, '') })} />
              </label>
              <label className="field"><span>Sted</span>
                <input type="text" value={levering.city} autoComplete="address-level2" maxLength={80}
                  onChange={(e) => setLevering({ ...levering, city: e.target.value })} />
              </label>
            </div>
            <label className="field"><span>Telefon (valgfritt)</span>
              <input type="text" inputMode="tel" value={levering.phone ?? ''} autoComplete="tel" maxLength={40}
                onChange={(e) => setLevering({ ...levering, phone: e.target.value })} />
            </label>
          </fieldset>
          <p className="ldb-kilde">Faktura kommer fra Phoniq.</p>
          {melding && <div className="notice bad">{melding}</div>}
          <button className="btn primary ldb-cta"
            disabled={opptatt || !levering.recipient.trim() || !levering.address.trim()
              || !/^\d{4}$/.test(levering.postcode) || !levering.city.trim()}
            onClick={() => void bestill()}>
            {opptatt ? 'Bestiller …'
              : `Bestill ${antall} bøker`}
          </button>
        </section>
      )}

      {steg === 'takk' && (
        <section className="ldb-steg ldb-takk">
          <div className="ldb-hake" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12.5l4.5 4.5L19 7.5" />
            </svg>
          </div>
          <h1 className="display">Bestilt!</h1>
          <p className="lead">
            {antall} lusedagbøker til {navn} er bestilt.
            Du får en bekreftelse på e-post nå, og vi sier fra når boken er på vei.
          </p>
          <p className="ldb-kilde">Neste gang holder det å trykke «Bestill igjen».</p>
          <Link className="btn primary ldb-cta" to={`/sites/${encodeURIComponent(id)}`}>
            Til anlegget
          </Link>
        </section>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ start */

function Start({ draft, navn, sist, siteId, onNy, onIgjen }: {
  draft: LiceBookDraft; navn: string; sist: LiceBook | null; siteId: string
  onNy: () => void; onIgjen: () => void
}) {
  if (sist) {
    const ordre = sist.orders?.[0]
    return (
      <section className="ldb-steg">
        <h1 className="display">Lusedagbok</h1>
        <p className="lead">Snart tom? Bestill samme bok en gang til.</p>
        <div className="ldb-sist">
          {sist.previews > 0
            ? <img src={liceBookPreview(siteId, sist.book_id, 0)} alt="" width={56} height={79} />
            : <MiniOmslag navn={navn} />}
          <div>
            <strong>{navn} · {draft.site.locality_no ?? '–'}</strong>
            <span>{sist.pens.length} merder · {sist.sheets} telleark</span>
            {ordre && (
              <span>Sist bestilt {new Date(ordre.created_at)
                .toLocaleDateString('nb-NO')} · {ordre.qty} stk</span>
            )}
          </div>
        </div>
        <button className="btn ghost ldb-lenke" onClick={onNy}>
          Ny generasjon eller nye numre? Lag ny bok
        </button>
        <button className="btn primary ldb-cta" onClick={onIgjen}>Bestill igjen</button>
      </section>
    )
  }
  return (
    <section className="ldb-steg">
      <div className="ldb-forside"><MiniOmslag navn={navn} stor /></div>
      <h1 className="display">Din egen lusedagbok</h1>
      <p className="lead">
        Ferdig trykt, med merdene deres og en QR-kode som hører til anlegget.
        Klar på tre minutter.
      </p>
      <ol className="ldb-punkter">
        <li>Bekreft anlegget</li>
        <li>Sjekk numrene på merdene</li>
        <li>Se boken og bestill</li>
      </ol>
      <button className="btn primary ldb-cta" onClick={onNy}>Lag lusedagbok</button>
    </section>
  )
}

function MiniOmslag({ navn, stor }: { navn: string; stor?: boolean }) {
  return (
    <div className={`ldb-omslag${stor ? ' stor' : ''}`} aria-hidden="true">
      <span className="ldb-omslag-merke"><i />Lusedagbok</span>
      <span className="ldb-omslag-navn">{navn}</span>
    </div>
  )
}

/* ---------------------------------------------------------------- merdene */

interface Prikk { key: string; label: string; x: number; y: number
                  state: 'fast' | 'valgt' | 'venter' }

/** Merdene som en skisse: plassert der de er tegnet, ellers i rekker på seks
 *  i gangrekkefølge -- samme regel som merdkartet i boka. */
function punkter(pens: { lat?: number | null; lon?: number | null }[]): [number, number][] {
  const alle = pens.length > 0 && pens.every((p) => p.lat != null && p.lon != null)
  if (alle) {
    const pts = pens.map((p) => ({ lat: p.lat!, lon: p.lon! }))
    const c = centroid(pts)!
    return pts.map((p) => { const [e, n] = toLocal(c, p); return [e, -n] })
  }
  return pens.map((_, i) => [i % 6, Math.floor(i / 6)])
}

function Skisse({ prikker, onTrykk, etikett }: {
  prikker: Prikk[]; onTrykk?: (key: string) => void; etikett: string
}) {
  const W = 340
  const geo = useMemo(() => {
    if (!prikker.length) return null
    const xs = prikker.map((p) => p.x), ys = prikker.map((p) => p.y)
    const x0 = Math.min(...xs), y0 = Math.min(...ys)
    const w = Math.max(...xs) - x0, h = Math.max(...ys) - y0
    const pad = 30
    const s0 = Math.min(w ? (W - 2 * pad) / w : Infinity, h ? (260 - 2 * pad) / h : Infinity)
    const s = Number.isFinite(s0) ? s0 : 0
    const H = Math.max(140, Math.min(300, h * s + 2 * pad))
    let dmin = Infinity
    for (let i = 0; i < prikker.length; i++)
      for (let j = i + 1; j < prikker.length; j++)
        dmin = Math.min(dmin, Math.hypot((xs[i]! - xs[j]!) * s, (ys[i]! - ys[j]!) * s))
    const r = Math.max(13, Math.min(22, Number.isFinite(dmin) ? dmin * 0.42 : 22))
    const ox = (W - w * s) / 2, oy = (H - h * s) / 2
    return { H, r, at: (p: Prikk) => [ox + (p.x - x0) * s, oy + (p.y - y0) * s] as const }
  }, [prikker])
  if (!geo) return null
  return (
    <svg className="ldb-skisse" viewBox={`0 0 ${W} ${geo.H}`} role="group" aria-label={etikett}>
      {prikker.map((p) => {
        const [cx, cy] = geo.at(p)
        const trykkbar = !!onTrykk && p.state === 'venter'
        return (
          <g key={p.key} className={`ldb-merd ${p.state}`}
            {...(trykkbar ? {
              role: 'button', tabIndex: 0,
              'aria-label': 'Merd uten nummer',
              onClick: () => onTrykk!(p.key),
              onKeyDown: (e: KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTrykk!(p.key) }
              },
            } : { 'aria-label': `Merd ${p.label}` })}>
            <circle cx={cx} cy={cy} r={geo.r} />
            <text x={cx} y={cy} dy="0.35em" textAnchor="middle"
              fontSize={Math.max(11, geo.r * 0.8)}>{p.label}</text>
          </g>
        )
      })}
    </svg>
  )
}

function Merdene({ id, draft, layout, onFerdig }: {
  id: string; draft: LiceBookDraft; layout: SiteLayout; onFerdig: () => Promise<void>
}) {
  const pens = layout.pens
  const kanEndre = draft.can_edit_layout === true
  const [omnummer, setOmnummer] = useState(false)
  const [trykket, setTrykket] = useState<string[]>([])
  const [nye, setNye] = useState<Record<string, string> | null>(null)
  const [oppsett, setOppsett] = useState<Oppsett>('to')
  const [antall, setAntall] = useState(8)
  const [opptatt, setOpptatt] = useState(false)
  const [melding, setMelding] = useState<string | null>(null)
  const [utenTegning, setUtenTegning] = useState(false)

  const xy = useMemo(() => punkter(pens), [pens])
  const tegning = kanEndre && !utenTegning ? draft.app_drawing : null

  function trykk(key: string) {
    if (trykket.includes(key)) return
    const neste = [...trykket, key]
    if (neste.length === pens.length) {
      setNye(Object.fromEntries(neste.map((k, i) => [k, String(i + 1)])))
      setTrykket([]); setOmnummer(false)
    } else {
      setTrykket(neste)
    }
  }

  async function lagre(body: Parameters<typeof saveSiteLayout>[1]) {
    setOpptatt(true); setMelding(null)
    try {
      await saveSiteLayout(id, body)
      invalidate(`/v1/sites/${encodeURIComponent(id)}`)
      await onFerdig()
    } catch (e) {
      setMelding(feil(e))
    } finally {
      setOpptatt(false)
    }
  }

  /* Det som lagres er hele anlegget, fordi PUT /layout er en setning om hele
     anlegget: en merd eller en flåte som ikke nevnes, tas ut. Derfor sendes
     alt tilbake som det var, med bare numrene og rekkefølgen endret. */
  function videreMedOppsett() {
    if (!nye) return void onFerdig()
    const rekkefolge = Object.fromEntries(
      Object.entries(nye).map(([k, v]) => [k, Number(v)]))
    void lagre({
      pens: pens.map((p) => ({
        pen_id: p.pen_id, pen_no: nye[p.pen_id] ?? p.pen_no, name: p.name,
        sort: (rekkefolge[p.pen_id] ?? 0) * 10 || p.sort,
        circumference_m: p.circumference_m, depth_m: p.depth_m,
        lat: p.lat ?? null, lon: p.lon ?? null, shape: p.shape,
        bearing_deg: p.bearing_deg ?? null,
      })),
      structures: layout.structures.map((s) => ({ ...s })),
    })
  }

  /* Tegningen fra appen er et forslag fra den som ba om boka, og blir
     anleggets oppsett først her. Merder som alt har samme nummer beholdes, så
     tellinger som er ført på dem følger med. */
  function brukTegning() {
    const finnes = new Map(pens.map((p) => [p.pen_no, p]))
    void lagre({
      pens: tegning!.pens.map((p, i) => ({
        pen_id: finnes.get(p.pen_no)?.pen_id, pen_no: p.pen_no,
        sort: (i + 1) * 10, shape: 'circle' as const,
        circumference_m: finnes.get(p.pen_no)?.circumference_m ?? DEFAULT_CIRCUMFERENCE_M,
        lat: p.lat, lon: p.lon,
      })),
      structures: layout.structures.map((s) => ({ ...s })),
    })
  }

  function videreMedFerdig() {
    const { lat, lon } = layout.site
    const pos = lat != null && lon != null ? plasser(oppsett, antall, lat, lon) : null
    void lagre({
      pens: Array.from({ length: antall }, (_, i) => ({
        pen_no: String(i + 1), sort: (i + 1) * 10, shape: 'circle' as const,
        circumference_m: DEFAULT_CIRCUMFERENCE_M,
        lat: pos?.[i]?.lat ?? null, lon: pos?.[i]?.lon ?? null,
      })),
      structures: layout.structures.map((s) => ({ ...s })),
    })
  }

  if (tegning) {
    return (
      <section className="ldb-steg">
        <h1 className="display">Tegnet i appen</h1>
        <p className="lead">
          {tegning.pens.length} merder, slik kunden la dem på kartet. Stemmer det med
          satellittbildet? Plassering og numre kan rettes etterpå.
        </p>
        <Skisse etikett="Tegningen fra appen" prikker={punkter(tegning.pens).map(([x, y], i) => ({
          key: String(i), label: tegning.pens[i]!.pen_no, x, y, state: 'fast' as const }))} />
        {melding && <div className="notice bad">{melding}</div>}
        <button className="btn primary ldb-cta" disabled={opptatt}
          onClick={brukTegning}>{opptatt ? 'Lagrer …' : 'Bruk tegningen'}</button>
        <button className="btn ghost ldb-lenke" onClick={() => setUtenTegning(true)}>
          Tegn selv i stedet
        </button>
      </section>
    )
  }

  if (pens.length === 0) {
    if (!kanEndre) {
      return (
        <section className="ldb-steg">
          <h1 className="display">Merdene mangler</h1>
          <p className="lead">
            Anlegget har ingen merder registrert ennå. Phoniq legger dem inn,
            eller en leder på anlegget kan gjøre det.
          </p>
        </section>
      )
    }
    return (
      <section className="ldb-steg">
        <h1 className="display">Hvordan ligger merdene?</h1>
        <p className="lead">Velg det som ligner mest. Numrene følger rekkefølgen dere teller i.</p>
        <div className="ldb-fliser">
          {OPPSETT.map((o) => (
            <button key={o.id} className="ldb-flis" aria-pressed={oppsett === o.id}
              onClick={() => setOppsett(o.id)}>
              <Skisse etikett={o.label} prikker={enheter(o.id, antall).map(([x, y], i) => ({
                key: String(i), label: String(i + 1), x, y, state: 'fast' as const }))} />
              <strong>{o.label}</strong>
            </button>
          ))}
        </div>
        <div className="ldb-teller">
          <strong>Antall merder</strong>
          <div className="row" style={{ gap: 4 }}>
            <button className="btn sm" aria-label="Færre merder" disabled={antall <= 1}
              onClick={() => setAntall(antall - 1)}>−</button>
            <span className="ldb-tall" aria-live="polite">{antall}</span>
            <button className="btn sm" aria-label="Flere merder" disabled={antall >= 40}
              onClick={() => setAntall(antall + 1)}>+</button>
          </div>
        </div>
        <Link className="btn ghost ldb-lenke"
          to={`/sites/${encodeURIComponent(id)}/oppsett?fra=lusedagbok`}>
          Ligger annerledes? Tegn på kartet
        </Link>
        {melding && <div className="notice bad">{melding}</div>}
        <button className="btn primary ldb-cta" disabled={opptatt}
          onClick={videreMedFerdig}>{opptatt ? 'Lagrer …' : 'Videre'}</button>
      </section>
    )
  }

  const prikker: Prikk[] = pens.map((p, i) => {
    const [x, y] = xy[i]!
    if (omnummer) {
      const k = trykket.indexOf(p.pen_id)
      return { key: p.pen_id, x, y, label: k >= 0 ? String(k + 1) : '',
               state: k >= 0 ? 'valgt' : 'venter' }
    }
    return { key: p.pen_id, x, y, label: nye?.[p.pen_id] ?? p.pen_no, state: 'fast' }
  })

  return (
    <section className="ldb-steg">
      <h1 className="display">{omnummer ? 'Trykk i tellerekkefølge' : 'Stemmer numrene?'}</h1>
      <p className="lead">
        {omnummer
          ? `Trykk på merdene i den rekkefølgen dere teller dem. ${trykket.length} av ${pens.length} er nummerert.`
          : 'Slik ligger anlegget i Phoniq. Numrene på kartet blir numrene i boken.'}
      </p>
      {kanViseKart(layout) ? (
        <MerdKartNummer layout={layout} etikett="Merdene på anlegget"
          merker={Object.fromEntries(prikker.map((p) => [p.key,
            { label: p.label, state: p.state }]))}
          onTrykk={omnummer ? trykk : undefined}
          reserve={<Skisse prikker={prikker} etikett="Merdene på anlegget"
            onTrykk={omnummer ? trykk : undefined} />} />
      ) : (
        <Skisse prikker={prikker} etikett="Merdene på anlegget"
          onTrykk={omnummer ? trykk : undefined} />
      )}
      {kanEndre && !omnummer && (
        <button className="btn ghost ldb-lenke"
          onClick={() => { setOmnummer(true); setTrykket([]) }}>
          {nye ? 'Nummerer på nytt igjen' : 'Nummerer på nytt'}
        </button>
      )}
      {omnummer && (
        <button className="btn ghost ldb-lenke"
          onClick={() => { setOmnummer(false); setTrykket([]) }}>
          Avbryt og behold numrene
        </button>
      )}
      {/* The book prints the rings where they are drawn, on the cover and on
          the instructions page -- so moving them belongs here too, in the
          same editor the farm was set up with. */}
      {kanEndre && !omnummer && (
        <Link className="btn ghost ldb-lenke"
          to={`/sites/${encodeURIComponent(id)}/oppsett?fra=lusedagbok`}>
          Ligger de annerledes? Tegn på kartet
        </Link>
      )}
      {!kanEndre && (
        <p className="ldb-kilde">Feil numre? En leder på anlegget eller Phoniq kan endre dem.</p>
      )}
      {nye && !omnummer && (
        <p className="ldb-kilde">De nye numrene lagres når du går videre. Tellinger som
          alt er ført, følger merden og ikke nummeret.</p>
      )}
      {melding && <div className="notice bad">{melding}</div>}
      <button className="btn primary ldb-cta" disabled={omnummer || opptatt}
        onClick={videreMedOppsett}>
        {omnummer ? `${trykket.length} av ${pens.length} nummerert`
          : opptatt ? 'Lagrer …' : 'Videre'}
      </button>
    </section>
  )
}

/* ------------------------------------------------------------------ boken */

function Boken({ id, bok, gammel, opptatt, melding, tilgjengelig, onLag, onVidere }: {
  id: string; bok: LiceBook | null; gammel: boolean; opptatt: boolean
  melding: string | null; tilgjengelig: boolean
  onLag: () => void; onVidere: () => void
}) {
  const [side, setSide] = useState(0)
  const klar = bok && !gammel

  // Lages første gang skjermen åpnes, og på nytt bare hvis noe er endret.
  useEffect(() => {
    if (!klar && !opptatt && !melding && tilgjengelig) onLag()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [klar, tilgjengelig])

  const n = klar ? Math.max(1, bok.previews) : 0
  return (
    <section className="ldb-steg">
      <h1 className="display">Slik blir boken</h1>
      <p className="lead">
        {klar
          ? `A5 med spiral øverst · ${bok.sheets} telleark · ${bok.counts} tellinger · ${bok.pages} sider`
          : 'A5 med spiral øverst.'}
      </p>
      {!tilgjengelig && (
        <div className="notice">Boktrykkeren er ikke satt opp på denne serveren ennå.</div>
      )}
      {melding && <div className="notice bad">{melding}</div>}
      {klar ? (
        <>
          <div className="ldb-bla">
            <button className="btn ghost sm" aria-label="Forrige side"
              onClick={() => setSide((side + n - 1) % n)}>‹</button>
            <img className="ldb-side" src={liceBookPreview(id, bok.book_id, side)}
              alt={`${SIDENAVN[side] ?? 'Side'} i lusedagboken`} />
            <button className="btn ghost sm" aria-label="Neste side"
              onClick={() => setSide((side + 1) % n)}>›</button>
          </div>
          <p className="ldb-sidenavn">
            {SIDENAVN[side] ?? `Side ${side + 1}`} · {side + 1} av {n}
            {' · '}<a href={liceBookPdf(id, bok.book_id)} target="_blank" rel="noopener">Hele boken (PDF)</a>
          </p>
          <button className="btn primary ldb-cta" onClick={onVidere}>Ser bra ut</button>
        </>
      ) : (
        <>
          <div className="ldb-lager" aria-live="polite">
            {opptatt ? 'Setter sidene …' : ' '}
          </div>
          {melding && tilgjengelig && (
            <button className="btn primary ldb-cta" onClick={onLag}>Prøv igjen</button>
          )}
        </>
      )}
    </section>
  )
}

/* ---------------------------------------------------------------- QR-koden */

// The stores, from the build: set VITE_LUSEDAGBOK_APP_STORE and
// VITE_LUSEDAGBOK_GOOGLE_PLAY in .env when the listings exist. Until then the
// page says the app is coming rather than linking nowhere.
const APP_STORE = (import.meta.env.VITE_LUSEDAGBOK_APP_STORE as string | undefined) || ''
const GOOGLE_PLAY = (import.meta.env.VITE_LUSEDAGBOK_GOOGLE_PLAY as string | undefined) || ''

/** /l/:code -- der QR-koden i boka lander når appen IKKE er installert.
 *
 *  Med appen installert kommer ingen hit: telefonen åpner adressen i appen
 *  (apple-app-site-association i Caddyfile), og appen velger anlegget fra
 *  koden. Hit kommer telefonen uten appen, og siden har én jobb: få appen på
 *  telefonen. Én knapp, til butikken for den telefonen det er.
 *
 *  Vises uten innlogging, og sier derfor ingenting om hvilket anlegg koden
 *  tilhører. Er man innlogget med tilgang, får man i tillegg en vei til
 *  anlegget i portalen -- spurt om stille, uten api-klienten, så et 401 ikke
 *  sender noen til innloggingen. */
export function Bokkode({ code }: { code: string }) {
  const kode = code.toUpperCase()
  const [anlegg, setAnlegg] = useState<string | null>(null)
  useEffect(() => {
    let levende = true
    fetch(`/v1/book-codes/${encodeURIComponent(kode)}`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (levende && d?.site_id) setAnlegg(d.site_id) })
      .catch(() => {})
    return () => { levende = false }
  }, [kode])

  const ua = navigator.userAgent
  const plattform = /iPhone|iPad|iPod/.test(ua) ? 'ios' : /Android/.test(ua) ? 'android' : 'annen'
  const butikker = [
    { navn: 'App Store', url: APP_STORE, for: 'ios' },
    { navn: 'Google Play', url: GOOGLE_PLAY, for: 'android' },
  ].filter((b) => plattform === 'annen' || b.for === plattform)

  return (
    <div className="ldb-ut">
      <div className="ldb">
        <section className="ldb-steg">
          <span className="ldb-merkelinje"><i />Lusedagbok</span>
          <h1 className="display">Tell med appen</h1>
          <p className="lead">
            Ta bilde av tellearket, så havner tellingen på riktig anlegg.
          </p>
          {butikker.map((b) => b.url ? (
            <a key={b.navn} className="btn primary ldb-cta" href={b.url}>
              Last ned fra {b.navn}
            </a>
          ) : (
            <button key={b.navn} className="btn primary ldb-cta" disabled>
              Kommer snart på {b.navn}
            </button>
          ))}
          <a className="btn ghost ldb-lenke" href={`lusedagbok://l/${encodeURIComponent(kode)}`}>
            Har du appen? Åpne den
          </a>
          {anlegg && (
            <Link className="btn ghost ldb-lenke" to={`/sites/${encodeURIComponent(anlegg)}`}>
              Åpne anlegget i portalen
            </Link>
          )}
        </section>
      </div>
    </div>
  )
}
